import { FC, memo, ReactNode, useCallback } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { TooltipGroupAnchor } from '@/components/shared/tooltip/TooltipGroup';
import Text from '@/storybook/components/Text/Text';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import type { ChatbotConversation } from '@/models/chatbot';
import { formatHistoryTime, HistoryTimeLabels } from './formatHistoryTime';
import ChatHistoryItemTitle from './ChatHistoryItemTitle';
import ChatHistoryItemMenu from './ChatHistoryItemMenu';
import { CHAT_HISTORY_TEST_IDS } from './testIds';
import styles from './ChatHistoryPanel.module.css';

interface ChatHistoryListItemProps {
  conversation: ChatbotConversation;
  timeLabels: HistoryTimeLabels;
  isOpening: boolean;
  isSelected: boolean;
  isRenaming: boolean;
  highlight?: string;
  truncationRecheckSignal?: number;
  onOpen: (conversationId: string) => void;
  onStartRename: (conversation: ChatbotConversation) => void;
  onDelete: (conversation: ChatbotConversation) => void;
  onRenameAnimationEnd: (conversationId: string) => void;
}

const ChatHistoryListItem: FC<ChatHistoryListItemProps> = ({
  conversation,
  timeLabels,
  isOpening,
  isSelected,
  isRenaming,
  highlight,
  onOpen,
  onStartRename,
  onDelete,
  onRenameAnimationEnd,
}) => {
  const { t } = useTranslation();
  const title = conversation.title.trim() || t('components.aiChatDrawer.history.untitled');

  const handleOpen = useCallback(() => onOpen(conversation.id), [onOpen, conversation.id]);
  const handleRename = useCallback(() => onStartRename(conversation), [onStartRename, conversation]);
  const handleDelete = useCallback(() => onDelete(conversation), [onDelete, conversation]);
  const handleAnimationEnd = useCallback(
    () => onRenameAnimationEnd(conversation.id),
    [onRenameAnimationEnd, conversation.id],
  );

  let trailing: ReactNode = null;
  if (isOpening) {
    trailing = (
      <LoadingSpinner
        hideLoadingMessage
        size={16}
        className={styles.spinner}
        testId={CHAT_HISTORY_TEST_IDS.itemLoading}
      />
    );
  } else if (conversation.updated_at !== undefined) {
    trailing = (
      <Text as="span" className={styles.itemTime} aria-hidden="true">
        {formatHistoryTime(conversation.updated_at, timeLabels)}
      </Text>
    );
  }

  return (
    <li className={cx(styles.item, { [styles.itemSelected]: isSelected })} data-testid={CHAT_HISTORY_TEST_IDS.item}>
      <button
        type="button"
        className={styles.itemTitle}
        onClick={handleOpen}
        disabled={isOpening}
        aria-current={isSelected ? 'true' : undefined}
      >
        <Text as="div" className={styles.itemTitleText}>
          <TooltipGroupAnchor value={title} isTruncatedOptions={{ ignoreWindowResize: true }}>
            <ChatHistoryItemTitle
              text={title}
              animate={isRenaming}
              highlight={highlight}
              onAnimationEnd={handleAnimationEnd}
            />
          </TooltipGroupAnchor>
        </Text>
        {trailing}
      </button>
      <div className={styles.itemActions}>
        <ChatHistoryItemMenu onRename={handleRename} onDelete={handleDelete} />
      </div>
    </li>
  );
};

export default memo(ChatHistoryListItem);
